/**
 * Caravan cart drawer behaviour
 * Paired with sections/cw-cart-drawer.liquid + assets/cw-cart-drawer.css (wave 27)
 *
 * Link from theme.liquid (just before </body>):
 *   {{ 'cw-cart-drawer.js' | asset_url | script_tag }}
 *
 * Responsibilities:
 *   1. Open/close: cart icon click, 'cart:open' event, esc, backdrop, close button
 *   2. Re-render on cart:updated (from wave-26 quick-add): fetch /cart.js, replace items HTML + totals
 *   3. Qty stepper: + / - buttons POST to /cart/change.js
 *   4. Remove: POST quantity=0 to /cart/change.js
 *   5. Note save: POST to /cart/update.js with note field
 *   6. Cross-sell add: handled by the existing data-cw-quick-add wiring in cw-product-card.js
 *
 * ~180 lines, no dependencies.
 */
(function () {
  'use strict';

  var SHIPPING_THRESHOLD_CENTS = 20000;  // configurable via window.CW_SHIPPING_THRESHOLD
  var MIX6_TARGET = 6;                    // configurable via window.CW_MIX6_TARGET

  if (typeof window.CW_SHIPPING_THRESHOLD === 'number') SHIPPING_THRESHOLD_CENTS = window.CW_SHIPPING_THRESHOLD * 100;
  if (typeof window.CW_MIX6_TARGET === 'number') MIX6_TARGET = window.CW_MIX6_TARGET;

  // Fix wave 68 (M18): install a shared reference-counted scroll lock on
  // window.CwScrollLock so multiple overlays (cart drawer, quickview, signup
  // modal) don't clobber each other's body.overflow state. First overlay
  // locks; last overlay to close unlocks.
  if (!window.CwScrollLock) {
    window.CwScrollLock = {
      count: 0,
      lock: function () {
        this.count += 1;
        if (this.count === 1) document.body.style.overflow = 'hidden';
      },
      unlock: function () {
        this.count = Math.max(0, this.count - 1);
        if (this.count === 0) document.body.style.overflow = '';
      }
    };
  }

  // ─── Open/close ───────────────────────────────────────────────────
  function open() {
    var drawer = document.querySelector('[data-cw-cartdrawer]');
    if (!drawer) return;
    if (drawer.getAttribute('data-open') === '1') return;  // already open; no double-lock
    drawer.setAttribute('data-open', '1');
    drawer.setAttribute('aria-hidden', 'false');
    window.CwScrollLock.lock();
  }
  function close() {
    var drawer = document.querySelector('[data-cw-cartdrawer]');
    if (!drawer) return;
    if (drawer.getAttribute('data-open') !== '1') return;  // already closed; no underflow
    drawer.setAttribute('data-open', '0');
    drawer.setAttribute('aria-hidden', 'true');
    window.CwScrollLock.unlock();
  }

  document.addEventListener('click', function (e) {
    // Open triggers
    var openBtn = e.target.closest('[data-cart-toggle], [href="/cart"], [href$="/cart"], .cart-link, .header__cart-toggle, [data-cart-icon]');
    if (openBtn && !e.target.closest('[data-cw-cartdrawer]')) {
      // Don't intercept if there's no drawer (fallback to cart page)
      if (document.querySelector('[data-cw-cartdrawer]')) {
        e.preventDefault();
        open();
        refresh();
        return;
      }
    }
    // Close triggers
    if (e.target.closest('[data-cw-cart-close]')) {
      close();
      return;
    }
    // Qty stepper
    var qtyBtn = e.target.closest('[data-cw-cart-qty]');
    if (qtyBtn) {
      var line = parseInt(qtyBtn.dataset.cwLineId, 10);
      var direction = qtyBtn.dataset.cwCartQty;
      var qtyEl = qtyBtn.parentNode.querySelector('[data-cw-line-qty]');
      // Fix wave 68 (M20): null-check qtyEl in case the items list was
      // re-rendered between click registration and handler firing. Without
      // the guard, qtyEl.textContent would throw on the detached node.
      if (!qtyEl) return;
      var current = parseInt(qtyEl.textContent, 10) || 0;
      var newQty = direction === 'inc' ? current + 1 : Math.max(0, current - 1);
      changeLine(line, newQty);
      return;
    }
    // Remove
    var removeBtn = e.target.closest('[data-cw-cart-remove]');
    if (removeBtn) {
      var rmLine = parseInt(removeBtn.dataset.cwLineId, 10);
      changeLine(rmLine, 0);
      return;
    }
    // Note save
    var noteBtn = e.target.closest('[data-cw-cart-note-save]');
    if (noteBtn) {
      var noteField = document.querySelector('[data-cw-cart-note]');
      if (noteField) saveNote(noteField.value, noteBtn);
      return;
    }
    // Free-bottle choice (drawer strip and cart-page strip share this)
    var giftBtn = e.target.closest('[data-cw-free-bottle-add]');
    if (giftBtn) {
      e.preventDefault();
      addFreeBottle(giftBtn.dataset.cwFreeBottleAdd, giftBtn);
      return;
    }
  });

  // Esc to close — fix wave 68 (M19): bail early if this drawer isn't open,
  // so the keydown doesn't clobber other overlays' close state.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var drawer = document.querySelector('[data-cw-cartdrawer]');
    if (drawer && drawer.getAttribute('data-open') === '1') close();
  });

  // 'cart:open' event opens the drawer programmatically
  window.addEventListener('cart:open', function () { open(); refresh(); });

  // 'cart:updated' event (fired by quick-add in cw-product-card.js) opens + refreshes
  window.addEventListener('cart:updated', function () { open(); refresh(); });

  // ─── Cart API helpers ────────────────────────────────────────────
  // Fix wave 63 (H5): all cart-mutation fetches now check r.ok, parse 422
  // error bodies (Shopify returns {status, message, description} on inventory
  // errors), and surface failures inline rather than silently corrupting the
  // rendered cart by feeding the error JSON to renderCart().
  function showCartError(msg) {
    var drawer = document.querySelector('[data-cw-cartdrawer]');
    if (!drawer) return;
    var slot = drawer.querySelector('[data-cw-cart-error]');
    if (!slot) {
      slot = document.createElement('div');
      slot.setAttribute('data-cw-cart-error', '');
      slot.setAttribute('role', 'alert');
      slot.style.cssText = 'padding: 10px 14px; margin: 10px 16px; background: #fde2e4; color: #7a1a26; border-radius: 6px; font-size: 13px; line-height: 1.4;';
      var itemsRoot = drawer.querySelector('[data-cw-cart-items]');
      if (itemsRoot && itemsRoot.parentNode) {
        itemsRoot.parentNode.insertBefore(slot, itemsRoot);
      } else {
        drawer.appendChild(slot);
      }
    }
    slot.textContent = msg || 'Something went wrong — try refreshing the cart.';
    slot.style.display = 'block';
    // Auto-dismiss after 6 seconds
    clearTimeout(slot._dismissTimer);
    slot._dismissTimer = setTimeout(function () { slot.style.display = 'none'; }, 6000);
  }

  function changeLine(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) {
          // Shopify returns 422 with {status, message, description} on errors
          // like quantity exceeds inventory. Pull the description, surface it,
          // and re-fetch ground truth via refresh() instead of feeding the
          // error body to renderCart.
          return r.json().then(function (err) {
            showCartError(err.description || err.message || 'Could not update quantity. Try refreshing.');
            return refresh();  // re-sync from /cart.js
          });
        }
        return r.json();
      })
      .then(function (cart) {
        // Only re-render if we got a real cart (defensive — refresh() above
        // returns whatever refresh resolved to; that path already called
        // renderCart so we'd double-render. Check for cart shape.)
        if (cart && typeof cart.item_count === 'number') {
          renderCart(cart);
        }
      })
      .catch(function (err) {
        console.error('[cw-cart-drawer] changeLine network failure', err);
        showCartError('Network problem — refresh and try again.');
      });
  }

  function saveNote(value, btn) {
    var originalText = btn.textContent;
    btn.textContent = 'Saving…';
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: value })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('saveNote response not ok');
        btn.textContent = '✓ Saved';
        setTimeout(function () { btn.textContent = originalText; }, 1600);
      })
      .catch(function (err) {
        console.error('[cw-cart-drawer] saveNote failed', err);
        btn.textContent = 'Error — retry';
        setTimeout(function () { btn.textContent = originalText; }, 2200);
      });
  }

  function refresh() {
    return fetch('/cart.js')
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js response not ok');
        return r.json();
      })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') {
          renderCart(cart);
        }
        return cart;
      })
      .catch(function (err) {
        console.error('[cw-cart-drawer] refresh failed', err);
        // Silent — refresh failure shouldn't show a UI error if the drawer
        // is just being opened. The user can interact and the next mutation
        // will surface a real error.
      });
  }

  // ─── Re-render ────────────────────────────────────────────────────
  function renderCart(cart) {
    if (!cart) return;
    // Count badges (header icon + drawer title both carry the attribute)
    document.querySelectorAll('[data-cw-cart-count]').forEach(function (countEl) {
      countEl.textContent = cart.item_count;
    });

    // Subtotal
    var subtotalEl = document.querySelector('[data-cw-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Progress bars
    updateShippingBar(cart);
    updateMix6Bar(cart);

    // Items list — replace innerHTML
    var itemsRoot = document.querySelector('[data-cw-cart-items]');
    if (itemsRoot) itemsRoot.innerHTML = renderItemsHTML(cart);
  }

  // ─── Free-bottle reward ───────────────────────────────────────────
  // A free-bottle line is any line carrying the _free_bottle property (set
  // by addFreeBottle below). Its price never counts toward the threshold,
  // whether or not the Shopify "Buy X get Y" discount has zeroed it yet.
  function isGiftLine(it) {
    return !!(it && it.properties && it.properties._free_bottle);
  }

  function updateShippingBar(cart) {
    var bar = document.querySelector('[data-cw-cart-shipping]');
    if (!bar) return;
    var giftCents = 0;
    var giftLines = [];
    cart.items.forEach(function (it, idx) {
      if (isGiftLine(it)) {
        giftCents += it.final_line_price;
        giftLines.push(idx + 1);
      }
    });
    var qualifying = cart.total_price - giftCents;
    var remaining = SHIPPING_THRESHOLD_CENTS - qualifying;
    if (remaining < 0) remaining = 0;
    var pct = Math.min(100, Math.round((qualifying / SHIPPING_THRESHOLD_CENTS) * 100));
    var fill = bar.querySelector('.cw-cartdrawer__bar-fill');
    if (fill) fill.style.width = pct + '%';
    var rem = bar.querySelector('[data-cw-cart-shipping-rem]');
    if (rem) rem.textContent = formatMoneyWhole(remaining);
    var done = qualifying >= SHIPPING_THRESHOLD_CENTS;
    bar.classList.toggle('cw-cartdrawer__bar--done', done);
    bar.classList.toggle('cw-cartdrawer__bar--gifted', giftLines.length > 0);

    // Cart dropped back under the threshold while a free bottle is in it:
    // take the bottle out again so nobody is charged for a "free" bottle.
    // Highest line first so indexes stay valid; renderCart re-runs after.
    if (!done && giftLines.length && !bar._cwRemovingGift) {
      bar._cwRemovingGift = true;
      var chain = Promise.resolve();
      giftLines.sort(function (a, b) { return b - a; }).forEach(function (line) {
        chain = chain.then(function () { return changeLine(line, 0); });
      });
      chain.then(function () { bar._cwRemovingGift = false; }, function () { bar._cwRemovingGift = false; });
    }
  }

  function addFreeBottle(variantId, btn) {
    if (btn.dataset.cwBusy === '1') return;
    btn.dataset.cwBusy = '1';
    var originalText = btn.textContent;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { _free_bottle: 'yes' } }] })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            showCartError(err.description || err.message || 'Could not add the free bottle. Try again.');
            return null;
          });
        }
        return r.json();
      })
      .then(function (added) {
        btn.textContent = originalText;
        btn.dataset.cwBusy = '0';
        if (!added) return;
        // On the cart page the strip is server-rendered: reload to reflect it.
        if (/^\/cart\/?$/.test(window.location.pathname)) { window.location.reload(); return; }
        open();
        return refresh();
      })
      .catch(function (err) {
        console.error('[cw-cart-drawer] addFreeBottle failed', err);
        btn.textContent = originalText;
        btn.dataset.cwBusy = '0';
        showCartError('Network problem — refresh and try again.');
      });
  }

  function updateMix6Bar(cart) {
    var bar = document.querySelector('[data-cw-cart-mix6]');
    if (!bar) return;
    var wineQty = 0;
    cart.items.forEach(function (it) {
      var isWine = false;
      if (it.product_type && /wine/i.test(it.product_type)) isWine = true;
      if (Array.isArray(it.product_tags) && it.product_tags.indexOf('spirit:wine') >= 0) isWine = true;
      // Fix wave 63 (L19): removed dead-code check for `it.tags` — Shopify
      // /cart.js doesn't expose a `tags` field at line-item level. Branch
      // was never taken.
      if (isWine) wineQty += it.quantity;
    });
    var pct = Math.min(100, Math.round((wineQty / MIX6_TARGET) * 100));
    var fill = bar.querySelector('.cw-cartdrawer__bar-fill');
    if (fill) fill.style.width = pct + '%';
    var qtyEl = bar.querySelector('[data-cw-cart-mix6-qty]');
    if (qtyEl) qtyEl.textContent = wineQty;
    if (wineQty >= MIX6_TARGET) {
      bar.classList.add('cw-cartdrawer__bar--done');
    } else {
      bar.classList.remove('cw-cartdrawer__bar--done');
    }
  }

  function renderItemsHTML(cart) {
    if (cart.item_count === 0) {
      return [
        '<div class="cw-cartdrawer__empty">',
        '  <p><strong>Your cart\'s empty.</strong></p>',
        '  <p>Nothing to ship, nothing to chill. Have a look at the recent picks:</p>',
        '  <a href="/collections/all-products" class="cw-cartdrawer__empty-cta">Browse the cellar →</a>',
        '</div>'
      ].join('');
    }
    var html = '<ul>';
    cart.items.forEach(function (it, idx) {
      var line = idx + 1;
      html += '<li class="cw-cartdrawer__item" data-cw-line="' + line + '">';
      html += '  <a href="' + it.url + '" class="cw-cartdrawer__item-media">';
      if (it.image) html += '<img src="' + it.image.replace(/\.(jpg|jpeg|png|webp)$/i, '_x100.$1') + '" alt="' + escapeHtml(it.product_title) + '" loading="lazy">';
      html += '  </a>';
      var gift = isGiftLine(it);
      html += '  <div class="cw-cartdrawer__item-body">';
      if (gift) {
        html += '    <small class="cw-cartdrawer__item-vendor cw-cartdrawer__item-vendor--gift">Free bottle · orders over ' + formatMoneyWhole(SHIPPING_THRESHOLD_CENTS) + '</small>';
      } else {
        html += '    <small class="cw-cartdrawer__item-vendor">' + escapeHtml(it.vendor) + '</small>';
      }
      html += '    <a href="' + it.url + '" class="cw-cartdrawer__item-title">' + escapeHtml(it.product_title) + '</a>';
      if (it.variant_title && it.variant_title !== 'Default Title') {
        html += '    <small class="cw-cartdrawer__item-variant">' + escapeHtml(it.variant_title) + '</small>';
      }
      html += '    <div class="cw-cartdrawer__item-meta">';
      if (gift) {
        html += '      <span class="cw-cartdrawer__qty cw-cartdrawer__qty--fixed">× 1</span>';
        html += '      <span class="cw-cartdrawer__item-price">' + (it.final_line_price === 0 ? 'Free' : formatMoney(it.final_line_price)) + '</span>';
      } else {
        html += '      <div class="cw-cartdrawer__qty">';
        html += '        <button type="button" class="cw-cartdrawer__qty-btn" data-cw-cart-qty="dec" data-cw-line-id="' + line + '" aria-label="Decrease">−</button>';
        html += '        <span class="cw-cartdrawer__qty-num" data-cw-line-qty>' + it.quantity + '</span>';
        html += '        <button type="button" class="cw-cartdrawer__qty-btn" data-cw-cart-qty="inc" data-cw-line-id="' + line + '" aria-label="Increase">+</button>';
        html += '      </div>';
        html += '      <span class="cw-cartdrawer__item-price">' + formatMoney(it.final_line_price) + '</span>';
      }
      html += '    </div>';
      html += '    <button type="button" class="cw-cartdrawer__item-remove" data-cw-cart-remove data-cw-line-id="' + line + '">Remove</button>';
      html += '  </div>';
      html += '</li>';
    });
    html += '</ul>';
    return html;
  }

  function escapeHtml(s) {
    if (typeof s !== 'string') return '';
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // "$38" rather than "$38.00" for the reward strip — matches Liquid's
  // money_without_trailing_zeros; keeps cents when they are not zero.
  function formatMoneyWhole(cents) {
    return formatMoney(cents).replace(/\.00(?=\D*$)/, '');
  }
})();
